import { AGREEMENTS, MONTHLY_HOURS, TAX_YEARS, UNION_RATES } from './constants.js';
import type {
  CompanySettings,
  DayInput,
  DayRow,
  Employee,
  HourlyRates,
  HourSplit,
  PeriodCalc,
  TaxYear,
  UnionRates,
} from './types.js';

/**
 * Klukka payroll engine — Icelandic parameters, SGS/Efling-style agreements.
 *
 * The same module runs in both portals and in the API, so an employee's phone
 * and the employer's dashboard can never disagree about a number.
 *
 * Conventions:
 *  - A day's hours split into dagvinna (DV), yfirvinna 1 (YV1) and yfirvinna 2
 *    (YV2, weekends and public holidays).
 *  - Withholding is computed cumulatively across the period and the marginal
 *    difference is attributed to each worked day, so the day rows always sum
 *    exactly to the period totals.
 *  - Persónuafsláttur and bracket ceilings are prorated by the *full* period
 *    length, never by elapsed days — otherwise a part-finished month would get
 *    a whole month's credit and tax would nearly vanish.
 */

/** Effective hourly rates for one employee, honouring a per-employee agreement. */
export function rates(settings: CompanySettings, emp: Pick<Employee, 'monthlyWage' | 'agreementId'>): HourlyRates {
  const m = emp.monthlyWage;
  let yv1 = settings.yv1Pct;
  let yv2 = settings.yv2Pct;
  if (emp.agreementId) {
    const a = AGREEMENTS.find((x) => x.id === emp.agreementId);
    if (a) {
      yv1 = a.yv1Pct;
      yv2 = a.yv2Pct;
    }
  }
  return { dv: m / MONTHLY_HOURS, yv1: (m * yv1) / 100, yv2: (m * yv2) / 100 };
}

const MS_PER_YEAR = 3.15576e10;

/**
 * Continuous employment in years, as of `asOf` — the settings' reference date
 * by default. Asking for a past date matters: a rate that stepped up last month
 * must not be applied to money earned the year before.
 */
export function seniorityYears(
  emp: Pick<Employee, 'startDate'>,
  settings: CompanySettings,
  asOf?: string,
): number {
  if (!emp?.startDate) return 0;
  return (new Date(asOf ?? settings.today).getTime() - new Date(emp.startDate).getTime()) / MS_PER_YEAR;
}

/** Vacation accrual %, stepping up with seniority per the wage agreement's tiers. */
export function orlofPctFor(
  emp: Pick<Employee, 'startDate'> | null | undefined,
  settings: CompanySettings,
  asOf?: string,
): number {
  if (!emp?.startDate) return settings.orlofPct;
  const yrs = seniorityYears(emp, settings, asOf);
  if (settings.orlofT3Years != null && yrs >= settings.orlofT3Years) return settings.orlofT3Pct;
  if (settings.orlofT2Years != null && yrs >= settings.orlofT2Years) return settings.orlofT2Pct;
  return settings.orlofPct;
}

/** What one vacation day is worth in ISK — a full day at the day-work rate. */
export function orlofDayRate(
  emp: Pick<Employee, 'monthlyWage' | 'agreementId'>,
  settings: CompanySettings,
): number {
  return rates(settings, emp).dv * settings.dailyThreshold;
}

/** Union fee and employer fund rates — constants of the employee's active union. */
export function unionRatesFor(emp: Pick<Employee, 'union'> | null | undefined, settings: CompanySettings): UnionRates {
  const u = emp?.union ? UNION_RATES[emp.union] : undefined;
  return u ? { member: u.member, employer: u.employer } : { member: settings.unionPct, employer: settings.employerFundsPct };
}

/** Statutory withholding constants for the period's own tax year. */
export function taxConsts(year: number): TaxYear {
  return TAX_YEARS[year] ?? TAX_YEARS[2026];
}

/**
 * Withholding before any personal credit is applied.
 *
 * Split out from `taxOn` because a payslip and a tax remittance both have to
 * state the persónuafsláttur that was actually used, and the only honest way to
 * get that figure is the difference between the bill before the credit and the
 * bill after it.
 */
export function bracketTaxOn(taxable: number, C: TaxYear, factor: number): number {
  const b1 = C.t[0] * factor;
  const b2 = C.t[1] * factor;
  let t = Math.min(taxable, b1) * (C.r[0] / 100);
  if (taxable > b1) t += (Math.min(taxable, b2) - b1) * (C.r[1] / 100);
  if (taxable > b2) t += (taxable - b2) * (C.r[2] / 100);
  return t;
}

/**
 * Withholding on a taxable amount.
 * `factor` scales the monthly bracket ceilings and credit to the period length.
 */
export function taxOn(taxable: number, C: TaxYear, factor: number, creditUtil: number): number {
  return Math.max(0, bracketTaxOn(taxable, C, factor) - C.credit * factor * (creditUtil / 100));
}

/**
 * Persónuafsláttur proration, per Skatturinn:
 *   month = annual / 12, half-month = annual / 24,
 *   any other length = annual × days / 365.
 */
export function creditFactor(periodType: CompanySettings['periodType'], periodDays: number): number {
  if (periodType === 'month') return 1;
  if (periodType === 'half') return 0.5;
  return (12 * periodDays) / 365;
}

/** Split one day's hours into DV / YV1 / YV2. */
export function classify(day: Pick<DayInput, 'h' | 'sick' | 'vacation' | 'weekend' | 'holiday'>, settings: CompanySettings): HourSplit {
  const h = day.h || 0;
  // Paid absence is settled at the day-work rate for a standard day.
  if (day.sick || day.vacation) return { dvH: settings.dailyThreshold, yv1H: 0, yv2H: 0 };
  if (day.weekend || day.holiday) return { dvH: 0, yv1H: 0, yv2H: h };
  const dvH = Math.min(h, settings.dailyThreshold);
  return { dvH, yv1H: Math.max(0, h - settings.dailyThreshold), yv2H: 0 };
}

export function dayGross(
  day: Pick<DayInput, 'h' | 'sick' | 'vacation' | 'weekend' | 'holiday'>,
  r: HourlyRates,
  settings: CompanySettings,
): HourSplit & { gross: number } {
  const c = classify(day, settings);
  return { ...c, gross: c.dvH * r.dv + c.yv1H * r.yv1 + c.yv2H * r.yv2 };
}

export interface CalcOptions {
  /**
   * Settle the period rather than estimate it.
   *
   * Mid-period, the personal credit is scaled by how far through the period the
   * work has got: on day three of the month you have not yet earned a month's
   * persónuafsláttur, and granting it would show a net figure the payslip could
   * never honour.
   *
   * Once the period closes that reasoning inverts. Persónuafsláttur is a
   * monthly entitlement of the employee, not something earned per day worked —
   * somebody who worked the first week of August and nothing after is still
   * entitled to August's whole credit. Leaving the elapsed scaling in would
   * overtax every part-time worker and everyone who left mid-period.
   *
   * A payroll run sets this. The live portals do not, which is why an estimate
   * during the month can be higher than the payslip that settles it.
   */
  settle?: boolean;
}

/**
 * Full period calculation for one employee.
 *
 * @param days     period start through today (or period end for closed periods)
 * @param adjGross gross-pay corrections; taxed like wages
 */
export function calcPeriod(
  emp: Employee,
  days: DayInput[],
  settings: CompanySettings,
  adjGross = 0,
  opts: CalcOptions = {},
): PeriodCalc {
  const r = rates(settings, emp);
  const periodDays = days[0]?.periodDays || days.length || 1;
  const factor = creditFactor(settings.periodType, periodDays);
  const pensPct = settings.pensionEmpPct + (emp.extraPensionPct || 0);
  const creditUtil = emp.creditUtil == null ? 100 : emp.creditUtil;
  const ur = unionRatesFor(emp, settings);
  const prior = emp.priorUnusedCredit || 0;
  const taxYear = days[0] ? Number(days[0].date.slice(0, 4)) : new Date(settings.today).getFullYear();
  const C = taxConsts(taxYear);
  const taxFor = (taxable: number, f: number) => Math.max(0, taxOn(taxable, C, f, creditUtil) - prior);

  const rows: DayRow[] = [];
  let cumGross = 0;
  let cumNet = 0;
  let worked = 0;
  // The proration in force the last time tax was computed. Needed after the
  // loop to restate the pre-credit bill on the same basis the tax was charged.
  let lastTaxFactor = factor;

  days.forEach((day, i) => {
    const g = dayGross(day, r, settings);
    if (g.gross <= 0) {
      rows.push({ ...day, ...g, net: 0, cumGross, cumNet });
      return;
    }
    cumGross += g.gross;
    // Prorate against the position in the *full* period, not the elapsed slice.
    // A settled period uses the whole period's factor for every day, so the day
    // rows still sum exactly to the total while the credit is the month's.
    const elapsed = (i + 1) / periodDays;
    const pension = (cumGross * pensPct) / 100;
    const union = (cumGross * ur.member) / 100;
    lastTaxFactor = opts.settle ? factor : factor * elapsed;
    const tax = taxFor(cumGross - pension, lastTaxFactor);
    const netToDate = cumGross - pension - union - tax;
    rows.push({ ...day, ...g, net: netToDate - cumNet, cumGross, cumNet: netToDate });
    cumNet = netToDate;
    worked++;
  });

  const gross = cumGross + adjGross;
  const pension = (gross * pensPct) / 100;
  const union = (gross * ur.member) / 100;

  /*
   * Persónuafsláttur accrues with the calendar, not with hours worked.
   *
   * The loop prorates each day by its own position, which leaves the final
   * figure pinned to whenever the employee last happened to work. That makes a
   * live estimate lurch: clock in on a new day and a whole day's credit arrives
   * at once, so a few krónur of gross produce a few thousand krónur of extra
   * "net" — the ticking number moving far more than the work behind it. The
   * credit is an entitlement of the period, earned by time passing rather than
   * by hours logged, so the proration is measured in days elapsed.
   *
   * A settled period uses the whole period's factor, which is the same
   * statement made at its end.
   */
  lastTaxFactor = opts.settle ? factor : factor * (rows.length / periodDays);
  const tax = taxFor(gross - pension, lastTaxFactor);
  const net = gross - pension - union - tax;

  /*
   * Day rows are attributed marginally as the period runs, against the factor
   * in force on each day. The total above is stated against today's factor, so
   * the last worked day absorbs the difference — otherwise the day list on
   * screen would not add up to the total printed above it.
   */
  const delta = net - cumNet;
  if (delta !== 0) {
    let last = -1;
    for (let i = rows.length - 1; i >= 0; i--) {
      if (rows[i].gross > 0) { last = i; break; }
    }
    if (last >= 0) {
      rows[last].net += delta;
      for (let i = last; i < rows.length; i++) rows[i].cumNet += delta;
    }
  }

  // Persónuafsláttur actually applied: what the bill would have been without
  // any credit, less what is being withheld. Derived rather than recomputed, so
  // it always reconciles with the tax on the same payslip — including the
  // credit carried in from earlier periods, which reduces the bill the same way.
  const preCreditTax = bracketTaxOn(Math.max(0, gross - pension), C, lastTaxFactor);
  const creditUsed = Math.max(0, preCreditTax - tax);

  // Accrue day by day at the rate the employee's seniority earned on that day.
  // Two reasons: a period straddling a tier step accrues at both rates, and a
  // closed period keeps the figure it closed with instead of being revalued
  // every time today moves. It also makes a period's accrual exactly equal the
  // same days accrued over an arbitrary range, which is what lets the orlof
  // ledger and this calculation agree to the krona.
  const orlofPct = orlofPctFor(emp, settings, days[days.length - 1]?.date);
  const orlof =
    rows.reduce((s, x) => s + (x.gross * orlofPctFor(emp, settings, x.date)) / 100, 0) +
    (adjGross * orlofPct) / 100;
  const dayRate = orlofDayRate(emp, settings);
  const pensionEr = (gross * (settings.pensionErPct + (emp.extraPensionPct ? settings.matchExtraPct : 0))) / 100;
  const funds = (gross * ur.employer) / 100;
  const trygg = ((gross + orlof + pensionEr) * settings.tryggPct) / 100;
  const totalCost = gross + orlof + pensionEr + funds + trygg;

  const hours = rows.reduce<HourSplit>(
    (a, x) => ({ dvH: a.dvH + x.dvH, yv1H: a.yv1H + x.yv1H, yv2H: a.yv2H + x.yv2H }),
    { dvH: 0, yv1H: 0, yv2H: 0 },
  );

  return {
    rows,
    rates: r,
    gross,
    adjGross,
    pension,
    union,
    unionPctUsed: ur.member,
    fundsPctUsed: ur.employer,
    tax,
    preCreditTax,
    creditUsed,
    net,
    orlof,
    orlofPctUsed: orlofPct,
    pensionEr,
    funds,
    trygg,
    totalCost,
    hours,
    worked,
    orlofDays: dayRate > 0 ? orlof / dayRate : 0,
  };
}

/** Full employer cost of an arbitrary gross amount — used for per-day cost tables. */
export function employerCost(gross: number, settings: CompanySettings, emp: Employee, asOf?: string): number {
  const orlof = (gross * orlofPctFor(emp, settings, asOf)) / 100;
  const pensionEr = (gross * (settings.pensionErPct + (emp?.extraPensionPct ? settings.matchExtraPct : 0))) / 100;
  const funds = (gross * unionRatesFor(emp, settings).employer) / 100;
  const trygg = ((gross + orlof + pensionEr) * settings.tryggPct) / 100;
  return gross + orlof + pensionEr + funds + trygg;
}

/** Sum a set of period calcs (e.g. twelve months into a year-to-date figure). */
export function sumCalcs(list: PeriodCalc[]): PeriodCalc {
  const z: PeriodCalc = {
    rows: [],
    rates: list[0]?.rates ?? { dv: 0, yv1: 0, yv2: 0 },
    gross: 0, adjGross: 0, pension: 0, union: 0,
    unionPctUsed: list[0]?.unionPctUsed ?? 0,
    fundsPctUsed: list[0]?.fundsPctUsed ?? 0,
    tax: 0, preCreditTax: 0, creditUsed: 0, net: 0, orlof: 0,
    orlofPctUsed: list[0]?.orlofPctUsed ?? 0,
    pensionEr: 0, funds: 0, trygg: 0, totalCost: 0,
    hours: { dvH: 0, yv1H: 0, yv2H: 0 },
    worked: 0, orlofDays: 0,
  };
  const keys = ['gross', 'adjGross', 'net', 'tax', 'preCreditTax', 'creditUsed', 'pension', 'union', 'orlof', 'pensionEr', 'funds', 'trygg', 'totalCost', 'orlofDays', 'worked'] as const;
  for (const c of list) {
    for (const k of keys) z[k] += c[k];
    z.hours.dvH += c.hours.dvH;
    z.hours.yv1H += c.hours.yv1H;
    z.hours.yv2H += c.hours.yv2H;
    z.rows = z.rows.concat(c.rows);
  }
  return z;
}
